/**
 * The acquisition dialog.
 *
 * Opened from the catalogue's selection bar and from the shortlist. It shows
 * the exact query a run will use, the real number of companies behind it, and
 * what each enrichment depth would cost - then starts the run.
 *
 * The rule this screen exists to enforce: **nothing starts until the operator
 * has seen the real count.** A SIC code can hold 460,000 companies, and the
 * difference between a 20-second job and a two-hour one is invisible until
 * something asks Companies House. So Preview runs automatically on open (one
 * cheap `size=1` call per code) and again whenever a filter changes.
 *
 * Every class here comes from App.css's existing primitives - `.scrim`,
 * `.dialog`, `.field`, `.pill`, `.alert`, `.table`. Only the genuinely new
 * shapes (the two-column layout, the depth chooser, the stat row) are added,
 * in section 17.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { acquisitionApi } from '../lib/api.js';
import { IconAlert, IconCheck, IconPlay } from './Icons.jsx';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'dissolved', label: 'Dissolved' },
  { value: 'liquidation', label: 'Liquidation' },
  { value: 'administration', label: 'Administration' },
];

const DEPTHS = [
  {
    value: 'list',
    name: 'List only',
    blurb:
      'Name, number, status, type, incorporation date, full registered address and SIC codes. All of it arrives with the search result - no extra call per company.',
  },
  {
    value: 'profile',
    name: 'Profiles',
    blurb:
      'Adds previous names, accounts and confirmation-statement due dates, insolvency and charges flags, and agency/employer classification. One extra API call each - and the marginal value is smaller than it looks: list depth already carries name, status, type, address and SIC codes, and classification itself runs free from those without a profile fetch at all (see Reclassify on the registry).',
  },
  {
    value: 'financials',
    name: 'Financials',
    blurb:
      'Reads the filed balance sheet - net assets, employee count, a trading verdict and health flags - real figures from the company\'s own filed accounts, not estimates. Turnover and profit stay absent for most small companies regardless: UK law does not require them to file a P&L, so no depth can recover a figure that was never filed. Five extra calls each on top of list depth.',
  },
  {
    value: 'full',
    name: 'Full',
    blurb:
      'Resolves directors: persons with significant control first, ranked by ownership band, falling through to the officers list when control sits with another company. A genuine tie is flagged for a human rather than guessed. Also runs website discovery (domain guessing confirmed against the page, DuckDuckGo as the fallback) and email guessing (ranked candidate addresses per director, generated from the domain\'s own naming convention). Neither spends a Companies House call - both are paced by their own throttle instead. SMTP verification of a guessed address is off by default (SMTP_PROBE_ENABLED) and, even switched on, only ever confirms a minority of domains - guesses are still generated and ranked either way. Two extra Companies House calls each on top of financials depth - one when an individual PSC resolves it, two when the officer fallback is needed.',
  },
];

function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

export function AcquisitionDialog({ codes, onClose }) {
  const [companyStatus, setCompanyStatus] = useState(['active']);
  const [incorporatedFrom, setIncorporatedFrom] = useState('');
  const [incorporatedTo, setIncorporatedTo] = useState('');
  const [location, setLocation] = useState('');
  const [depth, setDepth] = useState('list');

  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Abort the in-flight preview when the filters change again before it lands,
  // so a slow earlier request cannot overwrite a newer answer.
  const abortRef = useRef(null);

  const filters = useMemo(
    () => ({
      sicCodes: codes,
      companyStatus,
      ...(incorporatedFrom ? { incorporatedFrom } : {}),
      ...(incorporatedTo ? { incorporatedTo } : {}),
      ...(location.trim() ? { location: location.trim() } : {}),
    }),
    [codes, companyStatus, incorporatedFrom, incorporatedTo, location],
  );

  const runPreview = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsPreviewing(true);
    setError(null);
    try {
      const result = await acquisitionApi.preview(filters, { signal: controller.signal });
      setPreview(result);
      setConfirmed(false);
    } catch (caught) {
      if (caught.name === 'AbortError') return;
      setError(caught.message ?? 'Could not get a count from Companies House.');
      setPreview(null);
    } finally {
      if (abortRef.current === controller) setIsPreviewing(false);
    }
  }, [filters]);

  // Preview on open and whenever the query changes. Debounced because the
  // location box types a character at a time, and every preview spends real API
  // calls out of the shared budget.
  useEffect(() => {
    const timer = setTimeout(() => void runPreview(), 400);
    return () => clearTimeout(timer);
  }, [runPreview]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isStarting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isStarting, onClose]);

  const chosen = preview?.totals?.find((row) => row.depth === depth) ?? null;
  const needsConfirmation = chosen?.requiresConfirmation ?? false;
  const canStart = preview !== null && !isStarting && (!needsConfirmation || confirmed);

  const start = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const result = await acquisitionApi.create({ ...filters, enrichmentDepth: depth });
      onClose(result);
    } catch (caught) {
      setError(caught.message ?? 'Could not start the run.');
      setIsStarting(false);
    }
  };

  const toggleStatus = (value) => {
    setCompanyStatus((current) => {
      const next = current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value];
      // Never leave the query with no status at all - that would silently mean
      // "every status", which is not what unticking the last box looks like.
      return next.length === 0 ? ['active'] : next;
    });
  };

  return (
    <div className="scrim" onClick={isStarting ? undefined : () => onClose()} role="presentation">
      <div
        className="dialog dialog-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="acquire-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="acquire-title">
              Acquire companies
            </h2>
            <p className="dialog-description">
              {codes.length} SIC code{codes.length === 1 ? '' : 's'}. Each code runs as its own job,
              one after another, so they cannot compete for the rate limit.
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm dialog-close" onClick={() => onClose()}>
            Close
          </button>
        </div>

        <div className="dialog-body acquire-grid">
          {/* --- The query ------------------------------------------------ */}
          <section className="acquire-col">
            <h3 className="acquire-heading">The query</h3>

            <div className="field">
              <span
                className="field-label tip"
                data-tip="Dissolved companies are acquirable but they are not leads, and including them roughly triples the volume on most codes."
              >
                Company status
              </span>
              <div className="pill-row">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`pill ${companyStatus.includes(option.value) ? 'is-active' : ''}`}
                    onClick={() => toggleStatus(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="acquire-row">
              <label className="field">
                <span
                  className="field-label tip"
                  data-tip="Incorporation date. Both ends are inclusive. Leave blank for the whole register - a large code is split into date windows automatically."
                >
                  Incorporated from
                </span>
                <input
                  type="date"
                  className="input"
                  value={incorporatedFrom}
                  onChange={(event) => setIncorporatedFrom(event.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Incorporated to</span>
                <input
                  type="date"
                  className="input"
                  value={incorporatedTo}
                  onChange={(event) => setIncorporatedTo(event.target.value)}
                />
              </label>
            </div>

            <label className="field">
              <span
                className="field-label tip"
                data-tip="Matches the registered office address as Companies House indexes it - a town, a county or a postcode area."
              >
                Location (optional)
              </span>
              <input
                type="text"
                className="input"
                placeholder="e.g. Manchester"
                value={location}
                onChange={(event) => setLocation(event.target.value)}
              />
            </label>

            <div className="field">
              <span className="field-label">Enrichment depth</span>
              <div className="depth-list">
                {DEPTHS.map((option) => {
                  const estimate = preview?.totals?.find((row) => row.depth === option.value);
                  return (
                    <label
                      key={option.value}
                      className={`depth-option ${depth === option.value ? 'is-active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="depth"
                        value={option.value}
                        checked={depth === option.value}
                        onChange={() => {
                          setDepth(option.value);
                          setConfirmed(false);
                        }}
                      />
                      <span className="depth-body">
                        <span className="depth-name">
                          {option.name}
                          {estimate?.notYetImplemented ? (
                            <span
                              className="badge badge-warn tip"
                              data-tip={`Recorded as requested, but this executes at ${
                                DEPTHS.find((d) => d.value === estimate.executesAs)?.name ?? estimate.executesAs
                              } depth for now - the deepest built so far. The rest arrives in phases 7B-10.`}
                            >
                              Runs as {DEPTHS.find((d) => d.value === estimate.executesAs)?.name ?? estimate.executesAs}
                            </span>
                          ) : null}
                        </span>
                        <span className="depth-blurb">{option.blurb}</span>
                        {estimate ? (
                          <span className="depth-cost mono">
                            {formatNumber(estimate.apiCalls)} calls · about {estimate.durationLabel}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </section>

          {/* --- What it will do ------------------------------------------ */}
          <section className="acquire-col">
            <h3 className="acquire-heading">
              What this will do {isPreviewing ? <span className="spinner" /> : null}
            </h3>

            {error ? (
              <div className="alert alert-error">
                <span className="alert-icon">
                  <IconAlert />
                </span>
                <span className="alert-body">{error}</span>
              </div>
            ) : null}

            {preview ? (
              <>
                <div className="stat-row">
                  <div className="stat">
                    <span className="stat-value">{formatNumber(preview.totalHits)}</span>
                    <span className="stat-label">companies</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{formatNumber(chosen?.apiCalls)}</span>
                    <span className="stat-label">API calls</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{chosen?.durationLabel ?? '—'}</span>
                    <span className="stat-label">estimated</span>
                  </div>
                </div>

                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Description</th>
                        <th className="table-num">Companies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.codes.map((row) => (
                        <tr key={row.sicCode}>
                          <td className="mono">{row.sicCode}</td>
                          <td>{row.description}</td>
                          <td className="table-num">
                            {row.hits === null ? (
                              <span
                                className="text-dim tip"
                                data-tip="Companies House did not answer for this code. Try again, or check the key on the API keys page."
                              >
                                unknown
                              </span>
                            ) : (
                              formatNumber(row.hits)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {preview.totalHits > 10000 ? (
                  <div className="alert alert-info">
                    <span className="alert-icon">
                      <IconAlert />
                    </span>
                    <span className="alert-body">
                      This is above the 10,000 companies a single Companies House query can return, so
                      the run splits it into incorporation-date windows automatically. Nothing is lost -
                      it just takes more calls.
                    </span>
                  </div>
                ) : null}

                {preview.rateLimit ? (
                  <p
                    className="text-sm text-dim tip"
                    data-tip="Background runs stop at a lower ceiling than interactive requests, so a long acquisition can never lock you out of the rest of the application."
                  >
                    Rate limit: <span className="mono">{preview.rateLimit.used}</span> of{' '}
                    <span className="mono">{preview.rateLimit.limit}</span> calls used in the current
                    5-minute window. Runs draw on a {preview.rateLimit.backgroundCeiling}-call budget,
                    leaving the rest for the app.
                  </p>
                ) : null}

                {needsConfirmation ? (
                  <label className="confirm-row">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <span className="text-sm">
                      I understand this needs about{' '}
                      <strong>{formatNumber(chosen?.apiCalls)} API calls</strong> and will take roughly{' '}
                      <strong>{chosen?.durationLabel}</strong>.
                    </span>
                  </label>
                ) : null}
              </>
            ) : (
              !error && <p className="text-sm text-dim">Counting companies at Companies House…</p>
            )}
          </section>
        </div>

        <div className="dialog-footer">
          <button type="button" className="btn btn-ghost" onClick={() => onClose()}>
            Cancel
          </button>
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn" onClick={() => void runPreview()} disabled={isPreviewing}>
            Re-count
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void start()}
            disabled={!canStart}
          >
            {isStarting ? <span className="spinner" /> : <IconPlay />}
            Start {codes.length > 1 ? `${codes.length} runs` : 'run'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Confirmation shown after the runs are queued, with a way into the log. */
export function AcquisitionStarted({ result, onClose, onView }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="queued-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="queued-title">
              <IconCheck /> Queued
            </h2>
            <p className="dialog-description">
              {result.runs.length} run{result.runs.length === 1 ? '' : 's'} queued. They execute one at
              a time so they cannot compete for the Companies House rate limit.
            </p>
          </div>
        </div>

        <div className="dialog-body">
          {result.depthNote ? (
            <div className="alert alert-warn">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">{result.depthNote}</span>
            </div>
          ) : null}

          <ul className="queued-list">
            {result.runs.map((run) => (
              <li key={run.uuid}>
                <span className="mono">{run.sicCode}</span>
                <span className="text-dim">{run.description}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="dialog-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn btn-primary" onClick={onView}>
            Watch progress
          </button>
        </div>
      </div>
    </div>
  );
}
